import { CheckIcon, TriangleDownIcon, TriangleUpIcon } from '@radix-ui/react-icons'
import * as Select from '@radix-ui/react-select'
import { useEffect, useState, type JSX } from 'react'
import * as styles from './select-box.css'

interface SelectBoxProps {
  /** Label text shown above the select */
  label: string
  /** Current value of the select */
  value?: string
  /** Callback fired when the value changes */
  onValueChange?: (value: string) => void
  /** Placeholder text shown when no option is selected */
  placeholder?: string
  /** Whether the select is disabled */
  disabled?: boolean
  /** HTML name attribute */
  name?: string
  /** HTML id attribute. Required for accessibility when using a label */
  id?: string
  /** Shows "(Optional)" text next to label. Do not use together with isRequired */
  isOptional?: boolean
  /** Shows asterisk (*) next to label. Do not use together with isOptional */
  isRequired?: boolean
  /** Array of options to display in the select */
  options: Array<{
    value: string
    label: string | JSX.Element
    disabled?: boolean
    hideDisabledCheckIcon?: boolean
  }>
}

export function SelectBox({
  label,
  value,
  onValueChange,
  placeholder = 'Select an option',
  disabled = false,
  name,
  id,
  isOptional,
  isRequired,
  options,
}: SelectBoxProps) {
  const [internalValue, setInternalValue] = useState<string>(value ?? '')

  useEffect(() => {
    setInternalValue(value ?? '')
  }, [value])

  const handleValueChange = (newValue: string) => {
    setInternalValue(newValue)
    onValueChange?.(newValue)
  }

  return (
    <div className={styles.inputWrapper}>
      <div className={styles.labelContainer}>
        <label htmlFor={id} className={styles.inputLabel}>
          {label}
          {isRequired && ' *'}
        </label>
        {isOptional && <span className={styles.optionalText}>(Optional)</span>}
      </div>
      <div data-disabled={disabled}>
        <Select.Root value={internalValue} onValueChange={handleValueChange} disabled={disabled}>
          <Select.Trigger className={styles.selectTrigger} id={id} aria-label={label} value={internalValue} data-value={internalValue}>
            <Select.Value placeholder={placeholder} />
            <Select.Icon>
              <TriangleDownIcon className={styles.chevronDown} />
              <TriangleUpIcon className={styles.chevronUp} />
            </Select.Icon>
          </Select.Trigger>
          <Select.Content className={styles.selectContent} position="popper" side="bottom" align="start" sideOffset={-21} avoidCollisions={false}>
            <Select.Viewport className={styles.selectViewport}>
              {options.map(option => (
                <Select.Item key={option.value} value={option.value} className={styles.selectItem} disabled={option.disabled}>
                  <Select.ItemText asChild>
                    <span>{option.label}</span>
                  </Select.ItemText>
                  {!option.hideDisabledCheckIcon &&
                    (option.disabled ? (
                      <CheckIcon className={styles.disabledCheckboxIcon} />
                    ) : (
                      <Select.ItemIndicator>
                        <CheckIcon className={styles.checkboxIcon} />
                      </Select.ItemIndicator>
                    ))}
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
      </div>
      {name && <input type="hidden" name={name} value={internalValue} />}
    </div>
  )
}
